import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCalcLeite } from './CalcleiteContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './Custos.css';
import Config from '../../config/config.json';
import tags from '../../config/tags.json';

// --- FUNÇÕES AUXILIARES DE FORMATAÇÃO ---
const formatCurrencyForDisplay = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const number = Number(String(value).replace(/\D/g, '')) / 100;
  if (isNaN(number)) return '';
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
};

const Custos = () => {
  const { dados, setDados } = useCalcLeite();
  const navigate = useNavigate();

  const [mostrarResultados, setMostrarResultados] = useState(false);
  const [mostrarAjuda, setMostrarAjuda] = useState(false);

  useEffect(() => {
    if (!dados.custos) {
      setDados(prev => ({
        ...prev,
        custos: {
          gastosVolumosos: '', gastosPastagens: '', gastosConcentradosMinerais: '',
          gastosColaboradores: '', despesasFinanceiras: '', outrosGastos: ''
        }
      }));
    }
    if (dados.resultadosCustos) {
      setMostrarResultados(true);
    }
  }, [dados.custos, dados.resultadosCustos, setDados]);

  const handleCurrencyChange = (e) => {
    const { name, value } = e.target;
    const rawValue = value.replace(/\D/g, '');
    setDados(prev => ({
      ...prev,
      custos: { ...prev.custos, [name]: rawValue }
    }));
  };

  const handleCalcular = () => {
    const leiteProduzido = (Number(dados.producao?.leiteProduzido) || 0) / 100;
    const depreciacao = parseFloat(dados.resultadosCapital?.depreciacao) || 0;
    const rentabilidadeCapital = parseFloat(dados.resultadosCapital?.rentabilidadeCapital) || 0;

    if (leiteProduzido === 0) {
      alert("O 'Leite Produzido' (da primeira tela) precisa ser maior que zero para calcular os custos por litro.");
      return;
    }

    const c = dados.custos;
    const gastosVolumosos = (Number(c.gastosVolumosos) || 0) / 100;
    const gastosPastagens = (Number(c.gastosPastagens) || 0) / 100;
    const gastosConcentradosMinerais = (Number(c.gastosConcentradosMinerais) || 0) / 100;
    const gastosColaboradores = (Number(c.gastosColaboradores) || 0) / 100;
    const despesasFinanceiras = (Number(c.despesasFinanceiras) || 0) / 100;
    const outrosGastos = (Number(c.outrosGastos) || 0) / 100;

    const custoOperacional = gastosVolumosos + gastosPastagens + gastosConcentradosMinerais +
                             gastosColaboradores + despesasFinanceiras + outrosGastos;

    const custoTotal = custoOperacional + depreciacao + rentabilidadeCapital;
    const custoOperacionalLitro = custoOperacional / leiteProduzido;
    const custoTotalLitro = custoTotal / leiteProduzido;

    const resultados = {
      custoOperacional: custoOperacional.toFixed(2),
      custoTotal: custoTotal.toFixed(2),
      custoOperacionalLitro: custoOperacionalLitro.toFixed(2),
      custoTotalLitro: custoTotalLitro.toFixed(2),
    };

    setDados(prev => ({ ...prev, resultadosCustos: resultados }));
    setMostrarResultados(true);
  };

  const formatCurrencyResult = (value) => {
    const num = parseFloat(value || 0);
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  // --- LÓGICA DO PDF ESTILIZADA ---
  const handleDownloadPDF = () => {
    if (!dados.custos || !dados.resultadosCustos) {
      alert("Por favor, calcule os resultados antes de gerar o PDF.");
      return;
    }
    const doc = new jsPDF();
    const c = dados.custos;
    const r = dados.resultadosCustos;
    
    // Cabeçalho com fundo azul
    doc.setFillColor(30, 144, 255); // #1e90ff
    doc.rect(0, 0, 210, 40, 'F'); // Cabeçalho com 40mm de altura
    doc.setTextColor(255, 255, 255); // Texto branco
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text("Relatório de Custos", 105, 20, { align: 'center' });

    // Data centralizada abaixo do título
    doc.setTextColor(200, 200, 200); // Cinza claro
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    const dataFormatada = new Date(dados.producao?.dataSelecionada).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
    doc.text(`Data: ${dataFormatada}`, 105, 30, { align: 'center' });

    // Adicionar imagens no cabeçalho
    const custosAgroImg = Config.colors.base64.custosagro;
    const appImg = Config.colors.base64[tags.tags.app];
    if (custosAgroImg) {
      doc.addImage(custosAgroImg, 'PNG', 10, 7.5, 25, 25); // Logo CustosAgro mais à esquerda
    }
    if (appImg) {
      doc.addImage(appImg, 'PNG', 175, 7.5, 25, 25); // Logo do app mais à direita
    }

    // Tabela de Dados Inseridos
    doc.setTextColor(80, 80, 80);
    doc.setFontSize(11);
    const dadosInseridosBody = [
      ['Gastos com Volumosos', `${formatCurrencyResult(Number(c.gastosVolumosos) / 100)}`],
      ['Gastos com Pastagens', `${formatCurrencyResult(Number(c.gastosPastagens) / 100)}`],
      ['Gastos com Concentrados e Minerais', `${formatCurrencyResult(Number(c.gastosConcentradosMinerais) / 100)}`],
      ['Gastos com Colaboradores', `${formatCurrencyResult(Number(c.gastosColaboradores) / 100)}`],
      ['Despesas Financeiras', `${formatCurrencyResult(Number(c.despesasFinanceiras) / 100)}`],
      ['Outros Gastos', `${formatCurrencyResult(Number(c.outrosGastos) / 100)}`],
    ];

    autoTable(doc, {
      startY: 50,
      head: [['Descrição', 'Valor Inserido']],
      body: dadosInseridosBody,
      theme: 'striped',
      headStyles: { fillColor: [44, 62, 80] },
    });

    // Tabela de Resultados Calculados
    const resultadosBody = [
      ['Custo Operacional', `${formatCurrencyResult(r.custoOperacional)}`],
      ['Custo Total', `${formatCurrencyResult(r.custoTotal)}`],
      ['Custo Operacional por Litro', `${formatCurrencyResult(r.custoOperacionalLitro)}`],
      ['Custo Total por Litro', `${formatCurrencyResult(r.custoTotalLitro)}`],
    ];

    autoTable(doc, {
      head: [['Indicador', 'Resultado Calculado']],
      body: resultadosBody,
      theme: 'grid',
      headStyles: { fillColor: [30, 144, 255] },
    });

    // Adicionar numeração de páginas
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.setTextColor(150, 150, 150);
      doc.text(`Relatório gerado por CustosAgro - Página ${i} de ${pageCount}`, 105, 287, { align: 'center' });
    }
    
    doc.save("relatorio_custos.pdf");
  };

  const handleLimparDados = () => {
    if (window.confirm("Tem certeza que deseja limpar os dados desta tela?")) {
      setDados(prev => {
        const { custos, resultadosCustos, ...resto } = prev;
        localStorage.setItem('calcLeiteData', JSON.stringify(resto));
        return resto;
      });
      setMostrarResultados(false);
    }
  };

  return (
    <div className="custos-container">
      <div className="custos-card">
        <h1>Custo Operacional e Total</h1>
        <p className="custos-subtitle">Preencha os gastos para calcular os custos de produção.</p>

        <div className="custos-form">
          <div className="custos-input-group">
            <label>Gastos com Volumosos (R$)</label>
            <input type="text" name="gastosVolumosos" placeholder="Ex: 5.000,00" value={formatCurrencyForDisplay(dados.custos?.gastosVolumosos)} onChange={handleCurrencyChange} />
          </div>
          <div className="custos-input-group">
            <label>Gastos com Pastagens (R$)</label>
            <input type="text" name="gastosPastagens" placeholder="Ex: 3.000,00" value={formatCurrencyForDisplay(dados.custos?.gastosPastagens)} onChange={handleCurrencyChange} />
          </div>
          <div className="custos-input-group">
            <label>Gastos com Concentrados e Minerais (R$)</label>
            <input type="text" name="gastosConcentradosMinerais" placeholder="Ex: 8.000,00" value={formatCurrencyForDisplay(dados.custos?.gastosConcentradosMinerais)} onChange={handleCurrencyChange} />
          </div>
          <div className="custos-input-group">
            <label>Gastos com Colaboradores (R$)</label>
            <input type="text" name="gastosColaboradores" placeholder="Ex: 6.000,00" value={formatCurrencyForDisplay(dados.custos?.gastosColaboradores)} onChange={handleCurrencyChange} />
          </div>
          <div className="custos-input-group">
            <label>Despesas Financeiras (R$)</label>
            <input type="text" name="despesasFinanceiras" placeholder="Ex: 500,00" value={formatCurrencyForDisplay(dados.custos?.despesasFinanceiras)} onChange={handleCurrencyChange} />
          </div>
          <div className="custos-input-group">
            <label>Outros Gastos (R$)</label>
            <input type="text" name="outrosGastos" placeholder="Ex: 2.000,00" value={formatCurrencyForDisplay(dados.custos?.outrosGastos)} onChange={handleCurrencyChange} />
          </div>
          <button className="custos-button" onClick={handleCalcular}>Calcular</button>
          <button className="custos-help-button" onClick={() => setMostrarAjuda(true)}>O que são "Outros Gastos"?</button>
        </div>

        {mostrarResultados && (
          <div className="custos-results">
            <h2>Resultados</h2>
            <ul>
              <li>Custo Operacional: <strong>{formatCurrencyResult(dados.resultadosCustos?.custoOperacional)}</strong></li>
              <li>Custo Total: <strong>{formatCurrencyResult(dados.resultadosCustos?.custoTotal)}</strong></li>
              <li>Custo Operacional por Litro: <strong>{formatCurrencyResult(dados.resultadosCustos?.custoOperacionalLitro)}</strong></li>
              <li>Custo Total por Litro: <strong>{formatCurrencyResult(dados.resultadosCustos?.custoTotalLitro)}</strong></li>
            </ul>
            <button className="custos-button pdf" onClick={handleDownloadPDF}>Baixar Relatório (PDF)</button>
          </div>
        )}

        <div className="custos-navigation">
          <button className="custos-button back" onClick={() => navigate('/calcleite/app/capitalCafe')}>Voltar para Capital</button>
          <button className="custos-button clean" onClick={handleLimparDados}>Limpar Dados</button>
          <button className="custos-button navigate" onClick={() => navigate("/calcleite/app/receitaCafe")}>Avançar para Receita</button>
        </div>
      </div>
      
      {mostrarAjuda && (
        <div className="ajuda-modal-backdrop" onClick={() => setMostrarAjuda(false)}>
          <div className="ajuda-modal-content" onClick={e => e.stopPropagation()}>
            <h2>Ajuda - Outros Gastos</h2>
            <p>
              "Outros Gastos" podem incluir: leite para bezerros; manutenção de benfeitorias, máquinas e equipamentos; vacinas e medicamentos; material para inseminação; material de ordenha; combustíveis e lubrificantes, etc.
            </p>
            <button onClick={() => setMostrarAjuda(false)}>Fechar</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Custos;